import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, View, KeyboardTypeOptions, Image } from "react-native";
import { Platform, TextInput as RNTextInput, TouchableOpacity } from "react-native";
import {
    RecyclerListView,
    DataProvider,
    RecyclerListViewProps,
    LayoutProvider,
} from "recyclerlistview";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import Callout from "../../ui_atoms/Callout";
import Modal, { ModalSize } from "../Modal";
import TextField from "../TextField";
import IconButton from "../IconButton";
import Loading from "../Loading";
import Constants from "expo-constants";
import { useDebounce } from "use-debounce";
import { WebImageSelection } from "../../atoms/web-image";
import { useI18n } from "../../hooks/i18n-hooks";
import { useImageIsValidImage } from "./WebImagePicker";
import { Alert } from "../Alert";
import ScreenCardHeadband from "../../screens/ScreenCardHeadband";

const ITEM_MARGIN_SIZE = 16;
const ITEMS_PER_ROW = 3;

type WebImage = {
    url: string;
    thumbnail: string;
    mimeType: string;
};

type ChooserRow = {
    cells: WebImage[];
};

export type WebImageChooserProps = {
    onSelect?: (selection: WebImageSelection) => void | Promise<void>;
    onDismiss: () => void;
    visible: boolean;
    placeholder?: string;
    emptyText?: string;
    searchKeyboardType?: KeyboardTypeOptions;
    itemsPerRow?: number;
};

type GoogleItem = {
    kind: string;
    title: string;
    link: string;
    mime: string;
    displayLink: string;
    image: {
        thumbnailLink: string;
        height: number;
        width: number;
        thumbnailHeight: number;
        thumbnailWidth: number;
    };
};

type ImageRowProps = {
    row: ChooserRow;
    onSelect?: (url: WebImage) => void | Promise<void>;
    width: number;
};

const ImageRow: React.FC<ImageRowProps> = ({ row, onSelect, width }) => {
    const styles = useStyles();
    const cells = row.cells;

    return (
        <View style={[styles.imageRow]}>
            {cells.map((item, idx) => (
                <TouchableOpacity
                    key={`idx-${idx}-${item.thumbnail}`}
                    style={{
                        flex: 1,
                        marginHorizontal: ITEM_MARGIN_SIZE / 4,
                        backgroundColor: "#eee",
                    }}
                    onPress={() => void onSelect?.(item)}
                >
                    <Image
                        style={[styles.image, { width: width - ITEM_MARGIN_SIZE / 2 }]}
                        source={{
                            uri: item.thumbnail,
                        }}
                    />
                </TouchableOpacity>
            ))}
        </View>
    );
};

const WebImageChooser: React.FC<WebImageChooserProps> = ({
    onSelect,
    onDismiss,
    visible,
    placeholder,
    emptyText,
    searchKeyboardType,
    itemsPerRow = ITEMS_PER_ROW,
}) => {
    const { colors, marginSize } = useTheme();
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
    const [isSearching, setIsSearching] = useState(false);
    const [currentIndex, setCurrentIndex] = useState<number>(1);
    const [error, setError] = useState<number | null>(null);
    const i18n = useI18n();
    const [rows, setRows] = useState<ChooserRow[]>([]);
    const [_currentRow, setCurrentRow] = useState<ChooserRow>({ cells: [] });
    const isImageValid = useImageIsValidImage();

    const textInput = React.useRef<RNTextInput | null>(null);
    const [viewWidth, setViewWidth] = useState(0);

    const styles = useStyles();

    const populateCells = useCallback(
        (newItems: WebImage[]) => {
            setCurrentRow((originalCurrentRow) => {
                const rowsToAdd: ChooserRow[] = [];
                newItems.forEach((item) => {
                    originalCurrentRow.cells.push(item);
                    if (originalCurrentRow.cells.length >= itemsPerRow) {
                        rowsToAdd.push(originalCurrentRow);
                        originalCurrentRow = { cells: [] };
                    }
                });
                setRows((currentRows) => {
                    return [...currentRows, ...rowsToAdd];
                });
                return originalCurrentRow;
            });
        },
        [itemsPerRow],
    );

    const searchItemsFunc = useCallback(
        async (filter: string, startIndex: number = 1) => {
            if (!filter) {
                return;
            }
            try {
                setError(null);
                setIsSearching(true);
                const baseUrl = "https://www.googleapis.com/customsearch/v1";
                const q = encodeURI(filter);
                const itemsPerPage = 10; // MAXIMUM IS 10
                const key = Constants.expoConfig?.extra?.googleApiKey;
                const cx = Constants.expoConfig?.extra?.googleSearchCx;
                const finalUrl = `${baseUrl}?q=${q}&key=${key}&cx=${cx}&searchType=image&fileType=png%2Cjpeg&num=${itemsPerPage}&start=${startIndex}`;
                const response = await fetch(finalUrl, {
                    headers: {
                        Accept: "application/json",
                    },
                });
                const data = await response.json();
                const nextStart = (data.queries?.nextPage?.[0].startIndex as number) || -1;
                const dataItems: GoogleItem[] = data.items || [];
                const error = data.error;
                if (error) {
                    setError(error.code as number);
                    return;
                }
                setCurrentIndex(nextStart);
                const newItems = dataItems.map((item: GoogleItem) => ({
                    url: item.link,
                    thumbnail: item.image.thumbnailLink,
                    mimeType: item.mime,
                }));
                populateCells(newItems);
            } finally {
                setIsSearching(false);
            }
        },
        [populateCells],
    );

    const onClose = useCallback(() => {
        void setSearchQuery("");
        void onDismiss();
    }, [onDismiss, setSearchQuery]);

    const onStartSearch = useCallback(async () => {
        setRows([]);
        await searchItemsFunc(debouncedSearchQuery, 1);
    }, [searchItemsFunc, debouncedSearchQuery]);

    useEffect(() => {
        if (debouncedSearchQuery.length > 1) {
            void onStartSearch();
        } else {
            setRows([]); // initial value
        }
    }, [onStartSearch, debouncedSearchQuery]);

    useEffect(() => {
        if (!visible) {
            // cleanup field
            void setSearchQuery("");
        }
    }, [visible]);

    const onSearchNext = useCallback(async () => {
        if (currentIndex == -1) {
            // reach the end of elements
            return;
        }
        await searchItemsFunc(debouncedSearchQuery, currentIndex);
    }, [searchItemsFunc, debouncedSearchQuery, currentIndex]);

    const dataProvider = useMemo(
        () =>
            new DataProvider((r1: WebImage, r2: WebImage) => {
                return r1.url !== r2.url;
            }),
        [],
    );

    const data = useMemo(() => dataProvider.cloneWithRows(rows), [dataProvider, rows]);

    const height = useMemo(
        () => (viewWidth - marginSize) / itemsPerRow - ITEM_MARGIN_SIZE / 4,
        [viewWidth, itemsPerRow, marginSize],
    );

    const rowRenderer = useCallback<RecyclerListViewProps["rowRenderer"]>(
        (_type, item: ChooserRow) => (
            <ImageRow
                row={item}
                onSelect={async (item) => {
                    // verify image integrity
                    if (!item.url || !item.mimeType) {
                        return;
                    }
                    //

                    // use the real image, as we will probably need to crop it
                    const isValid = await isImageValid(item.url);
                    if (isValid) {
                        await onSelect?.({ url: item.url, mimeType: item.mimeType });
                    } else {
                        Alert.alert(
                            i18n.t("image_chooser.error"),
                            i18n.t("image_chooser.invalid_image"),
                        );
                    }
                }}
                width={height}
            />
        ),
        [height, isImageValid, onSelect, i18n],
    );

    const renderFooter = useCallback(() => {
        return (
            <View style={{ height: 50, width: "100%" }}>{isSearching ? <Loading /> : null}</View>
        );
    }, [isSearching]);

    const layoutProvider = useMemo(
        () =>
            new LayoutProvider(
                () => 0,
                (_type, dim) => {
                    dim.width = viewWidth;
                    dim.height = height;
                },
            ),
        [viewWidth, height],
    );

    return (
        <Modal
            visible={visible}
            animationType="slide"
            hardwareAccelerated
            statusBarTranslucent
            // workaround to fix React native bug @see https://github.com/react-native-modal/react-native-modal/issues/516
            onShow={() => setTimeout(() => textInput.current?.focus(), 100)}
            size={ModalSize.full}
            onClose={onClose}
            hideCloseButton
        >
            <VStack
                gap={1}
                v={1}
                style={styles.stackView}
                onLayout={(e) => {
                    setViewWidth(e.nativeEvent.layout.width);
                }}
            >
                <View style={styles.topView}>
                    <TextField
                        ref={textInput}
                        autoFocus={Platform.OS !== "android"}
                        style={{
                            flexShrink: 1,
                            backgroundColor: colors.background,
                        }}
                        label={placeholder}
                        onChangeText={setSearchQuery}
                        value={searchQuery}
                        keyboardType={searchKeyboardType}
                        clearButtonMode="while-editing"
                    />
                    <IconButton
                        icon="close"
                        size={24}
                        style={styles.staticHeaderIcon}
                        onPress={onClose}
                    />
                </View>
                {viewWidth && (
                    <RecyclerListView
                        //forceNonDeterministicRendering={isDynamicHeight}
                        style={{
                            alignSelf: "stretch",
                            width: viewWidth,
                        }}
                        keyboardShouldPersistTaps="always"
                        layoutProvider={layoutProvider}
                        dataProvider={data}
                        rowRenderer={rowRenderer}
                        onEndReached={onSearchNext}
                        renderFooter={renderFooter}
                        onEndReachedThreshold={0.2}
                    />
                )}
                {error ? (
                    <ScreenCardHeadband mode="error" text={i18n.t("image_chooser.error")} />
                ) : null}
                {rows.length == 0 ? (
                    <View style={{ alignSelf: "stretch", marginHorizontal: marginSize }}>
                        {emptyText ? <Callout markdown={emptyText || ""} /> : null}
                    </View>
                ) : null}
            </VStack>
        </Modal>
    );
};

const useStyles = () => {
    const { gridSize, colors, marginSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                stackView: {
                    flex: 1,
                    flexDirection: "column",
                    backgroundColor: colors.background,
                },
                topView: {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginHorizontal: marginSize,
                },
                staticHeaderIcon: {
                    margin: 0,
                    marginLeft: gridSize / 2,
                },
                textField: {
                    flex: 0,
                    alignSelf: "stretch",
                    backgroundColor: colors.background,
                },
                imageRow: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                },
                image: {
                    resizeMode: "contain",
                    aspectRatio: 1.0,
                    flex: 0,
                    alignSelf: "stretch",
                    borderWidth: 1,
                    borderColor: colors.accentLighter,
                },
            }),
        [colors.background, colors.accentLighter, gridSize, marginSize],
    );
};

export default WebImageChooser;
